import { Box } from "@mui/material"
import { createFileRoute, retainSearchParams, useLoaderData } from "@tanstack/react-router"
import type { FC } from "react"
import { z } from "zod"

import { ProductVersionStatus } from "@/graphql/codegen/graphql"
import { ProductEquivalentGroupsContainer } from "@/screens/Products/components/Product/Information/EquivalentGroups/ProductEquivalentGroupsContainer"
import { LabelInformationContainer } from "@/screens/Products/components/Product/Information/LabelInformation/LabelInformationContainer"
import { ProductOriginContainer } from "@/screens/Products/components/Product/Information/Origin/ProductOriginContainer"
import { ProductComments } from "@/screens/Products/components/Product/Information/ProductComments"
import { ProductConditionsContainer } from "@/screens/Products/components/Product/Information/ProductConditions/ProductConditionsContainer"
import { ProductFilesContainer } from "@/screens/Products/components/Product/Information/ProductFiles/ProductFilesContainer"
import { ProductFormContainer } from "@/screens/Products/components/Product/Information/ProductForm/ProductFormContainer"
import { ProductPackageInformationContainer } from "@/screens/Products/components/Product/Information/ProductPackageInformationContainer"
import { ProductShippingDetailsContainer } from "@/screens/Products/components/Product/Information/ShippingDetails/ProductShippingDetailsContainer"
import { ProductTechnicalInformationContainer } from "@/screens/Products/components/Product/Information/TechnicalInformation/TechnicalInformationContainer"
import { productInformationLoader } from "@/screens/Products/routeLoader"

const SearchParamsValidator = z.object({
  version: z.number().optional(),
})

const ProductInformationContainer: FC = () => {
  const { productVersionInfo } = useLoaderData({
    from: "/app/product/$productId/edit/_layout/information",
  })

  const isPastProductVersion = productVersionInfo?.versionStatus === ProductVersionStatus.Past

  return (
    <Box className='flex flex-col gap-4'>
      <Box className='flex gap-4'>
        <ProductFormContainer isPastProductVersion={isPastProductVersion} />
        <ProductEquivalentGroupsContainer />
      </Box>
      <ProductShippingDetailsContainer isPastProductVersion={isPastProductVersion} />

      <Box className='flex gap-4'>
        <ProductPackageInformationContainer isPastProductVersion={isPastProductVersion} />
        <ProductTechnicalInformationContainer isPastProductVersion={isPastProductVersion} />
      </Box>

      <LabelInformationContainer isPastProductVersion={isPastProductVersion} />

      <ProductOriginContainer isPastProductVersion={isPastProductVersion} />

      <ProductConditionsContainer isPastProductVersion={isPastProductVersion} />

      <ProductComments />

      <ProductFilesContainer />
    </Box>
  )
}

export const Route = createFileRoute("/app/product/$productId/edit/_layout/information")({
  validateSearch: SearchParamsValidator,
  search: {
    middlewares: [retainSearchParams(["version"])],
  },
  loaderDeps: ({ search }) => ({ version: search.version }),
  loader: productInformationLoader,

  component: ProductInformationContainer,
})
